import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Clock } from "lucide-react";
import { AppNav } from "@/components/AppNav";
import { BrBox } from "@/components/BrBox";
import { BrBtn } from "@/components/BrBtn";
import { BrLabel } from "@/components/BrLabel";
import { BrTag } from "@/components/BrTag";
import { ChessBoard } from "@/chess/board/ChessBoard";
import {
  coordsFromSquare,
  fenToBoard,
  formatClock,
  formatSpent,
  moveDurations,
  parseClocks,
  parseIncrement,
  replayPgn,
  STARTING_FEN,
  uciSquares,
  uciToSan,
  useFixAllMistakes,
  useGame,
  useSolveMistake,
  offsetShadow,
  TYPE,
  formatDate,
  resultLetter,
  type PositionOut,
} from "@chess/shared";
import { useTheme } from "@/theme/ThemeProvider";
import { useViewport } from "@/util/viewport";

interface Props {
  onOpenSettings: () => void;
}

export function AnalysisPage({ onOpenSettings }: Props) {
  const p = useTheme();
  const { isMobile, width } = useViewport();
  const nav = useNavigate();
  const params = useParams<{ gameId: string }>();
  const { data: game, isLoading, error } = useGame(params.gameId ?? null);
  const solveMistake = useSolveMistake();
  const fixAll = useFixAllMistakes();

  async function solve(mistakeId: string | null) {
    if (!mistakeId || solveMistake.isPending) return;
    const ts = await solveMistake.mutateAsync(mistakeId);
    // Pass the originating game so the drill returns here when finished.
    nav(`/app/train/${ts.id}?from=${params.gameId}`);
  }

  async function fixAllMistakes() {
    if (fixAll.isPending) return;
    // The currently-filtered mistakes (mine / opponent / both), in move order.
    const ids = mistakes.map((m) => m.mistake_id).filter((x): x is string => !!x);
    if (ids.length === 0) return;
    const ts = await fixAll.mutateAsync(ids);
    nav(`/app/train/${ts.id}?from=${params.gameId}`);
  }

  const positions: PositionOut[] = game?.evaluation?.positions ?? [];
  const pgnPlies = useMemo(() => (game?.pgn ? replayPgn(game.pgn) : []), [game?.pgn]);
  const totalPlies = Math.max(positions.length, pgnPlies.length);

  // Per-ply remaining clock + time spent, parsed from the PGN's [%clk] tags.
  // (chess.com games carry these; Lichess games usually don't → all null.)
  const clocks = useMemo(() => parseClocks(game?.pgn), [game?.pgn]);
  const durations = useMemo(
    () => moveDurations(clocks, parseIncrement(game?.time_control)),
    [clocks, game?.time_control],
  );
  const hasClocks = clocks.some((c) => c != null);
  // Starting time (seconds) from "base+inc" or "base" — both sides begin here.
  const initialClock = (() => {
    const tc = game?.time_control ?? "";
    const m = tc.match(/^(\d+)/);
    return m ? Number(m[1]) : null;
  })();
  // Remaining clock for white/black at a given ply index (the clock AFTER that
  // ply for the mover; the other side's clock carries from their last move).
  // Before a side has moved, fall back to the initial clock so Black shows its
  // starting time on move 1 rather than "—".
  const clockAt = (color: "white" | "black", upToPly: number): number | null => {
    const wantOdd = color === "black"; // ply index even = white moved
    for (let i = Math.min(upToPly, clocks.length - 1); i >= 0; i--) {
      if (i % 2 === (wantOdd ? 1 : 0) && clocks[i] != null) return clocks[i];
    }
    return initialClock;
  };

  // Whose move produced position index `idx`? Even array index = White moved,
  // odd = Black moved. "Mine" = the mover's color matches the user's color.
  const userColor = game?.color ?? "white";
  const moverIsMine = (idx: number) =>
    (idx % 2 === 0 ? "white" : "black") === userColor;

  type Mistake = PositionOut & { idx: number; mine: boolean };
  const allMistakes: Mistake[] = positions
    .map((pos, idx) => ({ ...pos, idx, mine: moverIsMine(idx) }))
    .filter((pos) => pos.classification === "blunder" || pos.classification === "mistake");
  const myMistakes = allMistakes.filter((m) => m.mine);
  const oppMistakes = allMistakes.filter((m) => !m.mine);

  // Mistake filter: which mistakes to surface (chart markers + list). Default
  // to the user's own — the "learn from your mistakes" focus.
  const [mistakeFilter, setMistakeFilter] = useState<"mine" | "opponent" | "both">("mine");
  const mistakes =
    mistakeFilter === "mine" ? myMistakes : mistakeFilter === "opponent" ? oppMistakes : allMistakes;

  const firstMistakeIdx = mistakes[0]?.idx ?? 0;

  const [ply, setPly] = useState(firstMistakeIdx >= 0 ? firstMistakeIdx : 0);
  // Orient the board from the player's side by default; let them flip it.
  const [flipped, setFlipped] = useState(userColor === "black");
  useEffect(() => {
    setFlipped(userColor === "black");
  }, [userColor]);

  useEffect(() => {
    if (totalPlies <= 0) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName ?? "";
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key === "ArrowLeft") {
        setPly((m) => Math.max(0, m - 1));
      } else if (e.key === "ArrowRight") {
        setPly((m) => Math.min(totalPlies - 1, m + 1));
      } else if (e.key === "Home") {
        setPly(0);
      } else if (e.key === "End") {
        setPly(Math.max(0, totalPlies - 1));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [totalPlies]);
  // Keep the moves table scrolled so the active move sits in the middle.
  const movesScrollRef = useRef<HTMLDivElement | null>(null);
  const activeRowRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    const container = movesScrollRef.current;
    const row = activeRowRef.current;
    if (!container || !row) return;
    const target = row.offsetTop - container.clientHeight / 2 + row.clientHeight / 2;
    const max = container.scrollHeight - container.clientHeight;
    container.scrollTo({ top: Math.max(0, Math.min(max, target)), behavior: "smooth" });
  }, [ply, totalPlies]);

  const currentFen = positions[ply]?.fen ?? pgnPlies[ply]?.fenAfter ?? STARTING_FEN;
  const board = fenToBoard(currentFen);

  // White-POV eval (pawns) of the shown position, capped to ±8 for the bar.
  // Mate resolves to the extreme in the mating side's favour.
  const currentEval = (() => {
    const pos = positions[ply];
    if (!pos) return 0;
    if (pos.mate_in != null && pos.mate_in !== 0) return pos.mate_in > 0 ? 8 : -8;
    if (pos.eval_cp == null) return 0;
    return Math.max(-8, Math.min(8, pos.eval_cp / 100));
  })();

  // Highlight the move that led to the shown position (from→to squares).
  const lastUci = positions[ply]?.played_move ?? pgnPlies[ply]?.uci ?? null;
  const lastSquares = lastUci ? uciSquares(lastUci) : null;
  const lastMoveFrom = lastSquares ? coordsFromSquare(lastSquares.from) : null;
  const lastMoveTo = lastSquares ? coordsFromSquare(lastSquares.to) : null;

  // SAN of the move actually played to reach position[idx].
  const playedSan = (idx: number): string => {
    const pos = positions[idx];
    if (!pos?.played_move) return pgnPlies[idx]?.san ?? "?";
    return uciToSan(pos.fen_before ?? pgnPlies[idx]?.fenBefore, pos.played_move);
  };
  // SAN of the better move the mover actually had — the engine's best move at
  // the position they faced, i.e. the PREVIOUS ply's top PV (not this
  // position's best_move, which is the opponent's reply).
  const bestAltSan = (idx: number): string => {
    const pos = positions[idx];
    const fenBefore = pos?.fen_before ?? pgnPlies[idx]?.fenBefore ?? null;
    const prevBestUci = positions[idx - 1]?.pvs?.[0]?.move ?? null;
    if (prevBestUci) return uciToSan(fenBefore, prevBestUci);
    return "?";
  };

  const BOARD_DESKTOP = Math.min(520, Math.max(360, width - 720));

  if (isLoading) {
    return <Centered p={p}>Loading game…</Centered>;
  }
  if (error || !game) {
    return (
      <Centered p={p}>
        <div style={{ marginBottom: 12 }}>Could not load this game.</div>
        <BrBtn variant="ghost" onClick={() => nav("/app/profile")}>← Back</BrBtn>
      </Centered>
    );
  }

  const r = resultLetter(game.result);

  return (
    <div style={{ minHeight: "100vh", background: p.bg, color: p.fg, fontFamily: TYPE.body }}>
      <AppNav onOpenSettings={onOpenSettings} />
      <main
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: isMobile ? "18px 16px 56px" : "24px 32px 56px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, marginBottom: 18, flexWrap: "wrap" }}>
          <div>
            <BrLabel>GAME ANALYSIS · {game.external_game_id.slice(0, 10).toUpperCase()}</BrLabel>
            <h1
              style={{
                fontFamily: TYPE.display,
                fontSize: isMobile ? 28 : 40,
                fontWeight: 800,
                letterSpacing: isMobile ? -1 : -1.5,
                lineHeight: 1.05,
                margin: "4px 0 6px",
                textTransform: "uppercase",
              }}
            >
              You ({game.color === "white" ? "WHITE" : "BLACK"})
              <span style={{ margin: "0 12px", color: p.fgMuted }}>vs</span>
              <span style={{ fontFamily: TYPE.mono }}>{game.opponent_username ?? "?"}</span>
            </h1>
            <div
              style={{
                display: "flex",
                gap: 14,
                fontSize: 12,
                fontFamily: TYPE.mono,
                fontWeight: 700,
                color: p.fgMuted,
                letterSpacing: 0.5,
                textTransform: "uppercase",
                flexWrap: "wrap",
              }}
            >
              <span>{game.time_control ?? "?"}</span>
              <span>·</span>
              <span>{game.opening ?? "?"}</span>
              <span>·</span>
              <span>{formatDate(game.played_at)}</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
            <BrTag bg={r === "W" ? p.success : r === "L" ? p.danger : p.card} fg={r === "W" ? p.successFg : r === "L" ? p.dangerFg : p.fg}>
              {r === "W" ? "YOU WON" : r === "L" ? "YOU LOST" : "DRAW"}
            </BrTag>
            <BrTag bg={p.danger} fg={p.dangerFg}>{myMistakes.length} YOUR MISTAKES</BrTag>
            <BrTag bg={p.card} fg={p.fg}>{oppMistakes.length} OPP. MISTAKES</BrTag>
          </div>
        </div>

        {/* Row 1: board (left) + moves table (right), table matches board height */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : `${BOARD_DESKTOP + 40}px 1fr`,
            gap: isMobile ? 18 : 24,
            alignItems: "stretch",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {(() => {
              const boardSize = isMobile ? Math.min(360, width - 36) : BOARD_DESKTOP;
              const evalBarGap = 8;
              // EvalBar rendered width = content 18 + 3px border each side.
              const evalBarWidth = 18 + 6;
              const groupWidth = boardSize + evalBarGap + evalBarWidth;
              return (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, width: groupWidth, maxWidth: "100%", margin: "0 auto" }}>
                  {/* Top clock = the side currently at the top of the board (flip-aware). */}
                  {hasClocks && (() => {
                    const topColor: "white" | "black" = flipped ? "white" : "black";
                    return (
                      <ClockBar
                        p={p}
                        label={`${game.color === topColor ? "YOU" : game.opponent_username ?? topColor.toUpperCase()}`}
                        time={formatClock(clockAt(topColor, ply))}
                        active={ply % 2 === (topColor === "white" ? 0 : 1)}
                      />
                    );
                  })()}
                  <div style={{ display: "flex", alignItems: "stretch", gap: evalBarGap }}>
                    <EvalBar p={p} evalPawns={currentEval} height={boardSize} flipped={flipped} />
                    <ChessBoard palette={p} size={boardSize} position={board} lastMoveFrom={lastMoveFrom} lastMoveTo={lastMoveTo} flipped={flipped} animMs={260} />
                  </div>
                  {hasClocks && (() => {
                    const botColor: "white" | "black" = flipped ? "black" : "white";
                    return (
                      <ClockBar
                        p={p}
                        label={`${game.color === botColor ? "YOU" : game.opponent_username ?? botColor.toUpperCase()}`}
                        time={formatClock(clockAt(botColor, ply))}
                        active={ply % 2 === (botColor === "white" ? 0 : 1)}
                      />
                    );
                  })()}
                </div>
              );
            })()}

            <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
              <BrBtn size="sm" variant="surface" withShadow={false} onClick={() => setPly(0)}>⏮</BrBtn>
              <BrBtn size="sm" variant="surface" onClick={() => setPly((m) => Math.max(0, m - 1))}>‹</BrBtn>
              <BrBtn size="sm" variant="surface" onClick={() => setPly((m) => Math.min(totalPlies - 1, m + 1))}>›</BrBtn>
              <BrBtn size="sm" variant="surface" withShadow={false} onClick={() => setPly(Math.max(0, totalPlies - 1))}>⏭</BrBtn>
              <BrBtn size="sm" variant="surface" withShadow={false} onClick={() => setFlipped((f) => !f)}>⇅ Flip</BrBtn>
            </div>
          </div>

          <BrBox padding={0} shadowSize={5} label="MOVES" style={{ display: "flex", flexDirection: "column", maxHeight: isMobile ? "none" : "82vh" }}>
            <div
              style={{
                borderBottom: `2px solid ${p.border}`,
                padding: "10px 14px",
                display: "flex",
                gap: 10,
                alignItems: "center",
                justifyContent: "space-between",
                background: p.bg,
              }}
            >
              <BrLabel>{(game.opening ?? "GAME").toUpperCase()}</BrLabel>
              <span style={{ fontSize: 11, fontFamily: TYPE.mono, fontWeight: 700, color: p.fgMuted }}>
                {Math.ceil(totalPlies / 2)} MOVES
              </span>
            </div>
            <div ref={movesScrollRef} style={{ flex: 1, overflowY: "auto", padding: "0" }}>
              <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontFamily: TYPE.mono, fontSize: 13, fontWeight: 700 }}>
                <colgroup>
                  <col style={{ width: 30 }} />
                  <col />
                  <col />
                </colgroup>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${p.border}` }}>
                    {([null, "WHITE", "BLACK"] as const).map((side, hi) => (
                      <th
                        key={hi}
                        style={{
                          padding: hi === 0 ? "8px 6px" : "8px 12px",
                          fontSize: 9.5,
                          letterSpacing: 1,
                          color: p.fgMuted,
                          fontWeight: 700,
                          background: p.bg,
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        {side === null ? (
                          <div style={{ textAlign: "center" }}>#</div>
                        ) : (
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                            <span>{side}</span>
                            <span>{hasClocks ? "EVAL · CLOCK" : "EVAL"}</span>
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: Math.ceil(totalPlies / 2) }).map((_, i) => {
                    const wPly = i * 2;
                    const bPly = i * 2 + 1;
                    const wPos = positions[wPly];
                    const bPos = positions[bPly];
                    const isActiveRow = ply === wPly || ply === bPly;
                    return (
                      <tr
                        key={i}
                        ref={isActiveRow ? activeRowRef : undefined}
                        style={{ borderBottom: `1px solid ${p.border}` }}
                      >
                        <td style={{ padding: "0 10px", color: p.fgMuted, width: 28, fontSize: 11, textAlign: "center" }}>{i + 1}</td>
                        <MoveCell
                          p={p}
                          san={pgnPlies[wPly]?.san ?? (wPos ? playedSan(wPly) : "")}
                          mark={wPos?.classification}
                          active={ply === wPly}
                          onClick={() => setPly(wPly)}
                          evalText={evalLabel(wPos)}
                          spentText={formatSpent(durations[wPly] ?? null)}
                          clockText={hasClocks ? formatClock(clockAt("white", wPly)) : undefined}
                        />
                        <MoveCell
                          p={p}
                          san={pgnPlies[bPly]?.san ?? (bPos ? playedSan(bPly) : "")}
                          mark={bPos?.classification}
                          active={ply === bPly}
                          disabled={bPly >= totalPlies}
                          onClick={() => bPly < totalPlies && setPly(bPly)}
                          evalText={evalLabel(bPos)}
                          spentText={formatSpent(durations[bPly] ?? null)}
                          clockText={hasClocks && bPly < totalPlies ? formatClock(clockAt("black", bPly)) : undefined}
                        />
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ borderTop: `2px solid ${p.border}`, padding: 10, background: p.bg, display: "flex", gap: 6 }}>
              <BrBtn size="sm" variant="surface" style={{ flex: 1 }} onClick={() => navigator.clipboard?.writeText(game.pgn ?? "")}>
                Copy PGN
              </BrBtn>
              <BrBtn size="sm" variant="accent" style={{ flex: 1 }} onClick={() => nav("/app/train")}>
                Train all →
              </BrBtn>
            </div>
          </BrBox>
        </div>

        {/* Row 2: full-width evaluation chart */}
        {positions.length > 1 && (
          <BrBox padding={14} shadowSize={4} label="EVALUATION" style={{ marginTop: isMobile ? 18 : 24 }}>
            <EvalChart
              positions={positions}
              mistakes={mistakes}
              currentPly={ply}
              onHoverPly={setPly}
              w={isMobile ? Math.min(width - 60, 700) : Math.min(width - 80, 1180)}
              h={isMobile ? 200 : 280}
            />
          </BrBox>
        )}

        {/* Row 3: mistakes list with filter switch, two columns */}
        <div style={{ marginTop: isMobile ? 18 : 24 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
            <BrLabel>FIXABLE MISTAKES · {mistakes.length}</BrLabel>
            <div style={{ display: "flex", border: `2px solid ${p.border}`, boxShadow: offsetShadow(p, 2, 2) }}>
              {([
                ["mine", `MINE (${myMistakes.length})`],
                ["opponent", `OPPONENT (${oppMistakes.length})`],
                ["both", `BOTH (${allMistakes.length})`],
              ] as const).map(([key, label], idx) => {
                const on = mistakeFilter === key;
                return (
                  <button
                    key={key}
                    onClick={() => setMistakeFilter(key)}
                    style={{
                      background: on ? p.accent : p.card,
                      color: on ? p.accentFg : p.fg,
                      border: "none",
                      borderLeft: idx === 0 ? "none" : `2px solid ${p.border}`,
                      padding: "6px 12px",
                      fontFamily: TYPE.mono,
                      fontWeight: 800,
                      fontSize: 11,
                      letterSpacing: 0.5,
                      cursor: "pointer",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          {mistakes.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <BrBtn
                variant="primary"
                size="md"
                disabled={fixAll.isPending}
                onClick={fixAllMistakes}
                style={{ width: "100%" }}
              >
                {fixAll.isPending
                  ? "Building session…"
                  : `Fix all ${mistakes.length} ${
                      mistakeFilter === "mine" ? "of your" : mistakeFilter === "opponent" ? "opponent" : ""
                    } mistakes →`}
              </BrBtn>
            </div>
          )}
          {mistakes.length > 0 ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 10,
              }}
            >
              {mistakes.map((m) => (
                <div
                  key={m.ply}
                  style={{
                    background: m.classification === "blunder" ? p.danger : p.accent,
                    color: m.classification === "blunder" ? p.dangerFg : p.accentFg,
                    border: `2px solid ${p.border}`,
                    boxShadow: offsetShadow(p, 3, 3),
                    padding: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      background: p.bg,
                      color: p.fg,
                      border: `2px solid ${p.bg}`,
                      display: "grid",
                      placeItems: "center",
                      fontFamily: TYPE.mono,
                      fontWeight: 800,
                      fontSize: 13,
                      flexShrink: 0,
                    }}
                  >
                    {Math.ceil(m.ply / 2)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 9, fontFamily: TYPE.mono, fontWeight: 700, letterSpacing: 1, opacity: 0.8, marginBottom: 2 }}>
                      {m.mine ? "YOU" : "OPPONENT"}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 800, fontFamily: TYPE.mono }}>
                      <span style={{ textDecoration: "line-through", opacity: 0.85 }}>{playedSan(m.idx)}</span>
                      <span style={{ margin: "0 8px" }}>→</span>
                      <span style={{ background: p.bg, color: p.fg, padding: "0 5px", border: `2px solid ${p.bg}` }}>{bestAltSan(m.idx)}</span>
                    </div>
                  </div>
                  <BrBtn size="xs" variant="surface" withShadow={false} onClick={() => { setPly(m.idx); }}>View</BrBtn>
                  <BrBtn size="xs" variant="primary" disabled={!m.mistake_id || solveMistake.isPending} onClick={() => solve(m.mistake_id)}>Solve →</BrBtn>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontFamily: TYPE.mono, fontWeight: 700, fontSize: 13, color: p.fgMuted, padding: "8px 0" }}>
              No {mistakeFilter === "both" ? "" : mistakeFilter === "mine" ? "mistakes of yours" : "opponent mistakes"} in this view.
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function EvalBar({
  p,
  evalPawns,
  height,
  flipped,
}: {
  p: ReturnType<typeof useTheme>;
  evalPawns: number; // white-POV, already capped to ±8
  height: number;
  flipped: boolean;
}) {
  // Map [-8, +8] -> white share of the bar [0, 1]; 0 eval = half-and-half.
  const v = Math.max(-8, Math.min(8, evalPawns));
  const whiteShare = 0.5 + v / 16;
  // White sits at the board's bottom (top when flipped) and grows toward the
  // opposite end as White's advantage increases.
  const whitePct = `${(whiteShare * 100).toFixed(2)}%`;
  const blackPct = `${((1 - whiteShare) * 100).toFixed(2)}%`;
  const label = v > 0 ? `+${v.toFixed(1)}` : v.toFixed(1);
  return (
    <div
      title={`Eval ${label}`}
      style={{
        width: 18,
        height,
        flexShrink: 0,
        border: `3px solid ${p.border}`,
        boxShadow: offsetShadow(p, 3, 3),
        display: "flex",
        flexDirection: flipped ? "column-reverse" : "column",
        background: "#111",
        overflow: "hidden",
      }}
    >
      {/* Black portion (top half region) */}
      <div style={{ height: blackPct, background: "#111", transition: "height 260ms ease" }} />
      {/* White portion (bottom) */}
      <div style={{ height: whitePct, background: "#f5f5f5", transition: "height 260ms ease" }} />
    </div>
  );
}

function ClockBar({
  p,
  label,
  time,
  active,
}: {
  p: ReturnType<typeof useTheme>;
  label: string;
  time: string;
  active: boolean;
}) {
  return (
    <div
      style={{
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        padding: "5px 12px",
        background: active ? p.accent : p.card,
        color: active ? p.accentFg : p.fg,
        border: `2px solid ${p.border}`,
        fontFamily: TYPE.mono,
        fontWeight: 800,
      }}
    >
      <span style={{ fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </span>
      <span style={{ fontSize: 16, letterSpacing: -0.5, fontVariantNumeric: "tabular-nums" }}>{time}</span>
    </div>
  );
}

function MoveCell({
  p,
  san,
  mark,
  active,
  disabled = false,
  onClick,
  evalText,
  spentText,
  clockText,
}: {
  p: ReturnType<typeof useTheme>;
  san: string;
  mark?: PositionOut["classification"];
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  evalText?: string;
  spentText?: string;
  clockText?: string;
}) {
  const isBlunder = mark === "blunder";
  const isMistake = mark === "mistake";
  // Mistakes get a standout background even when not the active move, so they
  // don't hide behind a tiny "?" — blunders red, mistakes yellow/accent.
  let bg = "transparent";
  let fg = p.fg;
  if (isBlunder) {
    bg = p.danger;
    fg = p.dangerFg;
  } else if (isMistake) {
    bg = p.accent;
    fg = p.accentFg;
  }
  if (active && !isBlunder && !isMistake) {
    bg = p.selected;
    fg = "#ffffff";
  }
  const muted = isBlunder || isMistake || active ? { opacity: 0.85 } : { color: p.fgMuted };
  return (
    <td
      onClick={onClick}
      style={{
        padding: "6px 12px",
        cursor: disabled ? "default" : "pointer",
        background: bg,
        color: fg,
        outline: active ? `2px solid ${p.fg}` : "none",
        outlineOffset: -2,
        verticalAlign: "middle",
      }}
    >
      {/* 2x2: SAN | eval (top), clock-remaining | time-spent (bottom). */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", columnGap: 8, rowGap: 1, alignItems: "baseline" }}>
        <span style={{ fontWeight: 800 }}>
          {san}
          {isBlunder && <span style={{ marginLeft: 6 }}>??</span>}
          {isMistake && <span style={{ marginLeft: 6 }}>?</span>}
        </span>
        <span style={{ fontSize: 11, fontWeight: 800, textAlign: "right", ...muted }}>{evalText}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 10, fontWeight: 700, ...muted }}>
          {clockText && (
            <>
              <Clock size={9} strokeWidth={2.5} style={{ display: "block" }} />
              {clockText}
            </>
          )}
        </span>
        <span style={{ fontSize: 10, fontWeight: 700, textAlign: "right", ...muted }}>{spentText}</span>
      </div>
    </td>
  );
}

// "+1.4" / "-0.7" / "#3" white-POV eval label for a position.
function evalLabel(pos: PositionOut | undefined): string {
  if (!pos) return "";
  if (pos.mate_in != null) return `#${Math.abs(pos.mate_in)}`;
  if (pos.eval_cp == null) return "";
  const v = pos.eval_cp / 100;
  return `${v >= 0 ? "+" : ""}${v.toFixed(1)}`;
}

function EvalChart({
  positions,
  mistakes,
  currentPly,
  onHoverPly,
  w,
  h,
}: {
  positions: PositionOut[];
  mistakes: { idx: number; mine: boolean }[];
  currentPly: number;
  onHoverPly: (ply: number) => void;
  w: number;
  h: number;
}) {
  const p = useTheme();
  const pad = { l: 34, r: 14, t: 16, b: 24 };
  const cw = w - pad.l - pad.r;
  const ch = h - pad.t - pad.b;
  const lo = -8;
  const hi = 8;
  const range = hi - lo;
  const data = positions.map((pos) => {
    if (pos.mate_in != null) return pos.mate_in > 0 ? hi : lo;
    if (pos.eval_cp == null) return 0;
    return Math.max(lo, Math.min(hi, pos.eval_cp / 100));
  });
  if (data.length < 2) return null;
  const sx = (i: number) => pad.l + (i / (data.length - 1)) * cw;
  const sy = (v: number) => pad.t + (1 - (v - lo) / range) * ch;
  const midY = sy(0);
  const pts = data.map((v, i) => `${sx(i)},${sy(v)}`).join(" ");
  const x0 = sx(0);
  const xN = sx(data.length - 1);

  // Map a pointer x within the SVG to the nearest position index.
  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const xRatio = (e.clientX - rect.left) / rect.width; // 0..1 across full SVG width
    const xSvg = xRatio * w;
    const t = (xSvg - x0) / (xN - x0);
    const idx = Math.round(Math.max(0, Math.min(1, t)) * (data.length - 1));
    onHoverPly(idx);
  };

  const cursorX = sx(Math.max(0, Math.min(data.length - 1, currentPly)));

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{ display: "block", cursor: "crosshair" }}
      onMouseMove={onMove}
    >
      <defs>
        {/* Split the area fill at the zero line: above = White-winning fill,
            below = Black-winning fill. */}
        <clipPath id="evalAbove">
          <rect x={0} y={pad.t} width={w} height={midY - pad.t} />
        </clipPath>
        <clipPath id="evalBelow">
          <rect x={0} y={midY} width={w} height={pad.t + ch - midY} />
        </clipPath>
      </defs>

      {/* plot background so the white-winning fill stands out from the card */}
      <rect x={pad.l} y={pad.t} width={cw} height={ch} fill={p.evalFillBlack} opacity={0.06} />

      <polygon points={`${x0},${midY} ${pts} ${xN},${midY}`} fill={p.evalFillWhite} clipPath="url(#evalAbove)" />
      <polygon points={`${x0},${midY} ${pts} ${xN},${midY}`} fill={p.evalFillBlack} clipPath="url(#evalBelow)" />

      <line x1={pad.l} y1={midY} x2={w - pad.r} y2={midY} stroke={p.border} strokeWidth="1.5" strokeDasharray="5 4" opacity={0.6} />
      <text x={pad.l - 6} y={pad.t + 10} textAnchor="end" fontSize="10" fontFamily={TYPE.mono} fontWeight="700" fill={p.fgMuted}>+8</text>
      <text x={pad.l - 6} y={midY + 3} textAnchor="end" fontSize="10" fontFamily={TYPE.mono} fontWeight="700" fill={p.fgMuted}>0</text>
      <text x={pad.l - 6} y={pad.t + ch} textAnchor="end" fontSize="10" fontFamily={TYPE.mono} fontWeight="700" fill={p.fgMuted}>-8</text>

      <polyline points={pts} fill="none" stroke={p.fg} strokeWidth="2.5" />

      {mistakes.map(({ idx, mine }) => {
        const x = sx(idx);
        // Even idx = White moved, odd = Black moved.
        const whiteMoved = idx % 2 === 0;
        const color = whiteMoved ? p.mistakeWhite : p.mistakeBlack;
        return (
          <g key={idx}>
            <line x1={x} y1={pad.t} x2={x} y2={pad.t + ch} stroke={color} strokeWidth="2.5" strokeDasharray={whiteMoved ? "2 3" : undefined} />
            <rect x={x - 9} y={pad.t - 7} width="18" height="15" fill={color} stroke={p.border} strokeWidth="2" />
            <text x={x} y={pad.t + 5} textAnchor="middle" fontSize="10" fontWeight="800" fill="#ffffff" fontFamily={TYPE.body}>
              {mine ? "!" : "·"}
            </text>
          </g>
        );
      })}

      {/* current-move cursor */}
      <line x1={cursorX} y1={pad.t} x2={cursorX} y2={pad.t + ch} stroke={p.selected} strokeWidth="2" />
      <circle cx={cursorX} cy={sy(data[Math.max(0, Math.min(data.length - 1, currentPly))])} r={4} fill={p.selected} stroke={p.border} strokeWidth="1.5" />
    </svg>
  );
}

function Centered({ children, p }: { children: React.ReactNode; p: ReturnType<typeof useTheme> }) {
  return (
    <div style={{ minHeight: "100vh", background: p.bg, color: p.fg, display: "grid", placeItems: "center", padding: 16, fontFamily: TYPE.body, fontWeight: 700, textAlign: "center" }}>
      <div>{children}</div>
    </div>
  );
}
